import { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import {
  WorkflowBlock as WorkflowBlockType,
  Connector,
  BlockGroup,
  BlockType,
  BlockDimensions,
  PortSide,
} from '@/types/workflow';
import { type Point } from '@/lib/connectorUtils';
import { WorkflowBlock } from './WorkflowBlock';
import { ConnectorPath } from './ConnectorPath';
import { ConnectorContextMenu } from './ConnectorContextMenu';
import { useSelectionMode } from '@/contexts/SelectionModeContext';
import { GroupFrame } from './GroupFrame';
import { CollapsedGroupCard } from './CollapsedGroupCard';
import { GRID_SIZE_PX } from '@/constants/canvas';
import { computeGroupCenter } from '@/hooks/useLayerNavigation';
import { buildAnchorIndex } from '@/lib/connectorAnchorIndex';
import { computeContentBounds, fitToScreen } from '@/lib/fitToScreen';
import { CanvasHeader } from '@/gui/workflow/CanvasHeader';
import { CanvasHud } from '@/components/editor/hud/CanvasHud';
import { BlockContextMenu } from './BlockContextMenu';
import { LayerContextMenu } from './LayerContextMenu';
import { GroupContextMenu } from './GroupContextMenu';
import { CanvasContextMenu, type CanvasToolType } from './CanvasContextMenu';
import { dispatchOnboardingEvent } from '@/components/onboarding/uiAdapter';
import { useCanvasTouchGestures } from '@/functions/canvas-touch';
import { GestureTrail } from './GestureTrail';
import { SheetSwitchAnimation } from './SheetSwitchAnimation';
import { CanvasEmptyState } from './CanvasEmptyState';
import type { Point as GesturePoint } from '@/lib/gestures/quadrant';
import { useCanvasKeyboardNav } from '@/functions/canvas-keyboard-nav';
import { useCanvasViewport } from '@/functions/canvas-viewport';
import { useCanvasZoom } from '@/functions/canvas-zoom';
import { useCanvasDrag } from '@/functions/canvas-drag';
import { useCanvasSelection } from '@/functions/canvas-selection';

interface WorkflowCanvasProps {
  blocks: WorkflowBlockType[];
  connectors: Connector[];
  groups: BlockGroup[];
  selectedBlockId: string | null;
  selectedGroupId: string | null;
  connectMode: boolean;
  groupMode: boolean;
  groupSelection: string[];
  visibleBlocks: WorkflowBlockType[];
  visibleConnectors: Connector[];
  collapsedGroups: BlockGroup[];
  expandedGroups: BlockGroup[];
  breadcrumbs: Array<{ id: string | null; name: string }>;
  layerPath: string[];
  onNavigateInto: (groupId: string) => void;
  onNavigateToLevel: (index: number) => void;
  onSelectBlock: (id: string) => void;
  onSelectGroup: (id: string | null) => void;
  onCreateBlock: (type: BlockType, x: number, y: number, nameOverride?: string) => void;
  onUpdateBlockPosition: (id: string, x: number, y: number, moveGroup?: boolean) => void;
  onUpdateBlockIcon: (id: string, iconKey: string) => void;
  onConnect: (fromId: string, toId: string, fromPort?: PortSide, toPort?: PortSide) => void;
  onToggleGroupSelection: (id: string) => void;
  onCanvasClick: () => void;
  onMinimapDataChange: (data: MinimapData) => void;
  onUpdateGroupPosition: (id: string, x: number, y: number) => void;
  onConvertToLayer: (blockId: string) => void;
  onUpdateConnectorWaypoints: (id: string, waypoints: Point[]) => void;
  onUpdateConnectorLabel?: (id: string, label: string) => void;
  /** C6: right-click actions på connector-linjen. Om omitted → ingen meny. */
  onDeleteConnector?: (id: string) => void;
  /** Context-menu actions for blocks. When omitted, that menu item is hidden. */
  onFocusBlockProperty?: (blockId: string, field: 'name' | 'description' | 'color') => void;
  onCreateGroupFromSelection?: (blockIds: string[]) => void;
  onAddBlockToGroup?: (groupId: string, blockId: string) => void;
  onDuplicateBlock?: (blockId: string) => void;
  /** I-101: öppna ExplainBlockPanel. Om undefined visas inte menypunkten. */
  onExplainBlock?: (blockId: string) => void;
  onDeleteBlock?: (blockId: string) => void;
  /** Context-menu actions for groups (expanded AND collapsed) */
  onToggleGroupCollapse?: (groupId: string) => void;
  onDeleteGroup?: (groupId: string) => void;
  /** Toggles `locked` on a group (expanded group context menu only). */
  onToggleGroupLock?: (groupId: string) => void;
  /** Tool types available in the canvas right-click "Add block" submenu. */
  toolTypes?: CanvasToolType[];
  /** Cross-sheet linking state — true when the pick-a-block gesture is active. */
  crossSheetMode?: boolean;
  /** The block id currently marked as the cross-sheet link source (if any). */
  crossSheetSourceBlockId?: string | null;
  canvasInnerRef?: React.RefObject<HTMLDivElement>;
  viewOnly?: boolean;
  /** Show "Welcome / Wizard"-CTA inside the empty-state when canvas is empty. */
  onReopenWizard?: () => void;
  /**
   * Override the tier-derived zoom-mode. Used by the /preview route to
   * force `dynamic` (gränslös pan/zoom) when displaying over-limit
   * workflows that don't fit in the user's static-mode canvas. Without
   * this, a Free user previewing a 50-block multi-sheet workflow would
   * see content overflow the 1920×1080 fixed canvas.
   */
  forceZoomMode?: 'static' | 'dynamic';
  /**
   * Hide the built-in CanvasHeader zoom controls. Used by /preview which
   * renders its own floating zoom-panel — without this flag the user
   * would see two sets of zoom buttons (top-right inbuilt + floating
   * panel) on the same screen.
   */
  hideHeaderScaleControls?: boolean;
  /**
   * Mount the movable HUD container (search bar + zoom controls inside
   * one drag-able panel). When true:
   *   - CanvasHeader's inline zoom controls are hidden (forced
   *     `hideHeaderScaleControls`)
   *   - CanvasHud is rendered inside WorkflowCanvas with position
   *     persisted to localStorage
   *   - The page-level GlobalSearchMount should be hidden on this
   *     route (see App.tsx wiring) so the user doesn't see two
   *     SearchBars.
   * Defaults to false so legacy callers (Preview, /atlas) keep their
   * existing layout.
   */
  enableFloatingHud?: boolean;
  onRegisterZoomHandlers?: (handlers: { zoomIn: () => void; zoomOut: () => void; resetZoom: () => void; fitToScreen: () => void }) => void;

  /** Mobile gesture-handlers — alla optional, undefined = ignorera gesten. */
  mobileGestures?: MobileGestureHandlers;
}

export interface MobileGestureHandlers {
  /** Sheet-switch via 1-finger båg-svep. */
  onSheetSwitch?: (direction: 'fwd' | 'back') => void;
  /** 3x tap på tom canvas. */
  onOpenSheetList?: () => void;
  /** 2-finger tap → undo. */
  onUndo?: () => void;
  /** Long-press på block → context-meny vid screen-koord. */
  onBlockLongPress?: (blockId: string, screenX: number, screenY: number) => void;
  /** Long-press på collapsed-group-card. */
  onGroupCardLongPress?: (groupId: string, screenX: number, screenY: number) => void;
  /** Long-press på tom canvas → lägg-till-block-submenu. */
  onCanvasLongPress?: (screenX: number, screenY: number) => void;
  /** Edge-swipe vänster → öppna ToolSidebar. */
  onOpenLeftSidebar?: () => void;
  /** Edge-swipe höger → öppna PropertiesSheet. */
  onOpenRightSidebar?: () => void;
  /** Edge-swipe top → öppna TopBar-meny. */
  onOpenTopBar?: () => void;
}

export interface MinimapData {
  canvasWidth: number;
  canvasHeight: number;
  viewportWidth: number;
  viewportHeight: number;
  scrollLeft: number;
  scrollTop: number;
  zoom: number;
  blockDimensions: Record<string, BlockDimensions>;
  onNavigate: (x: number, y: number) => void;
}

export function WorkflowCanvas(props: WorkflowCanvasProps) {
  const {
    blocks, groups,
    selectedBlockId, selectedGroupId,
    connectMode, groupMode, groupSelection,
    visibleBlocks, visibleConnectors, collapsedGroups, expandedGroups,
    breadcrumbs, layerPath,
    onNavigateInto, onNavigateToLevel,
    onSelectBlock, onSelectGroup,
    onCreateBlock, onUpdateBlockPosition, onUpdateBlockIcon,
    onConnect, onToggleGroupSelection, onCanvasClick,
    onMinimapDataChange, onUpdateGroupPosition, onConvertToLayer,
    onUpdateConnectorWaypoints, onUpdateConnectorLabel, onDeleteConnector,
    onFocusBlockProperty, onCreateGroupFromSelection, onAddBlockToGroup,
    onDuplicateBlock, onDeleteBlock, onExplainBlock,
    onToggleGroupCollapse, onDeleteGroup, onToggleGroupLock,
    toolTypes = [],
    crossSheetMode = false, crossSheetSourceBlockId = null,
    canvasInnerRef, viewOnly, forceZoomMode, hideHeaderScaleControls, enableFloatingHud, onRegisterZoomHandlers,
    mobileGestures, onReopenWizard,
  } = props;

  const canvasRef = useRef<HTMLDivElement>(null);
  // Selected-connector state lives in SelectionModeContext so the right-
  // side PropertiesPanel can render the same connector form blocks/groups
  // use. Mutex with selectedBlockId/selectedGroupId is enforced upstream.
  const { selectedConnectorId, setSelectedConnectorId } = useSelectionMode();
  // A-008: scroll + resize → RAF-throttlade state-updates via extraherad hook.
  const { scrollPosition, viewportSize } = useCanvasViewport(canvasRef);
  const [blockDimensions, setBlockDimensions] = useState<Record<string, BlockDimensions>>({});

  // Zoom-mode + scale + canvas-bounds/origin + scroll-centrering —
  // extraherad till useCanvasZoom (#2 slice 1). Fit-to-screen bor kvar
  // här (behöver allBlockDimensions från buildAnchorIndex nedan).
  const {
    effectiveZoom,
    isStaticMode,
    useDynamicTransition,
    isMobile,
    contentScale,
    setContentScale,
    handleScaleUp,
    handleScaleDown,
    handleResetScale,
    coordSpaceDivider,
    computeBounds,
    frozenCanvasBoundsRef,
    dynamicCanvasWidth,
    dynamicCanvasHeight,
    canvasOriginX,
    canvasOriginY,
    canvasOriginXRef,
    canvasOriginYRef,
    getOrigin,
  } = useCanvasZoom({ canvasRef, blocks, groups, forceZoomMode });

  // Drag-motorn (block / group / collapsed / waypoint) — extraherad
  // till useCanvasDrag (#2 slice 2). State + speglande refs delas med
  // useCanvasTouchGestures nedan.
  const {
    setIsDragging,
    setDragBlockId,
    isDraggingRef,
    dragBlockIdRef,
    dragOffsetRef,
    handleBlockDragStart,
    handleCollapsedGroupDragStart,
    handleAddWaypoint,
    handleRemoveWaypoint,
    handleWaypointDragStart,
    handleMouseMove,
    handleMouseUp,
    handleGroupDragStart,
    handleGroupDragStartFromCoords,
    handleGroupDragMoveFromCoords,
  } = useCanvasDrag({
    canvasRef,
    viewOnly: viewOnly ?? false,
    blocks,
    groups,
    contentScale,
    computeBounds,
    frozenCanvasBoundsRef,
    getOrigin,
    onUpdateBlockPosition,
    onUpdateGroupPosition,
    onUpdateConnectorWaypoints,
    onSelectGroup,
    // Force a re-render efter bounds-thaw så liveBounds tas i bruk direkt.
    onBoundsThawed: () => setBlockDimensions(prev => ({ ...prev })),
  });

  // Selection / connect / kontextmeny — extraherad till
  // useCanvasSelection (#2 slice 3).
  const {
    connectFromId,
    connectorMenu,
    setConnectorMenu,
    handleCanvasContextMenuCapture,
    handleAddBlockFromMenu,
    handleBlockSelect,
    handlePortClick,
    handleCanvasClick,
    handleMinimapNavigate,
    handleGroupClick,
  } = useCanvasSelection({
    canvasRef,
    viewOnly: viewOnly ?? false,
    groupMode,
    contentScale,
    getOrigin,
    onSelectBlock,
    onToggleGroupSelection,
    onSelectGroup,
    onConnect,
    onCanvasClick,
    onCreateBlock,
  });

  // GestureTrail-overlayens path-data: skrivs av canvas-touch och läses
  // av <GestureTrail/>. Ref + tick-counter undviker re-render-storm.
  const gesturePathRef = useRef<GesturePoint[]>([]);
  const gestureTickRef = useRef(0);
  // Defensiv memoization: pathRefs-prop:en är en object literal. Hade
  // canvas-touch:s useEffect haft den i deps (vilket den hade i en
  // tidigare bugg-version) skulle re-binding-stormen knäcka pan/pinch.
  // Stable referens nu så framtida kod inte kan trigga buggen igen.
  const gesturePathRefs = useMemo(
    () => ({ pathRef: gesturePathRef, tickRef: gestureTickRef }),
    [],
  );

  // Sheet-switch-animation: trigger ändras varje gång ett båg-svep
  // commit:as → SheetSwitchAnimation visas i 300ms.
  const [sheetSwitchTrigger, setSheetSwitchTrigger] = useState<{
    key: number;
    direction: 'fwd' | 'back';
  } | null>(null);

  const handleMobileSheetSwitchInternal = useCallback((direction: 'fwd' | 'back') => {
    if (!mobileGestures?.onSheetSwitch) return;
    mobileGestures.onSheetSwitch(direction);
    setSheetSwitchTrigger({ key: Date.now(), direction });
  }, [mobileGestures]);

  // fit-to-screen-ref pekar på den faktiska implementationen som definieras
  // längre ner. Använd ref för att undvika temporal-dead-zone.
  const handleFitToScreenRef = useRef<() => void>(() => {});
  // Group-drag-handlers definieras längre ner (de behöver groups/blocks
  // state). Touch-hooken behöver dem direkt → ref-indirektion.
  const handleGroupDragStartFromCoordsRef = useRef<(groupId: string, screenX: number, screenY: number) => void>(() => {});
  const handleGroupDragMoveFromCoordsRef = useRef<(groupId: string, currentX: number, currentY: number) => void>(() => {});

  useCanvasTouchGestures({
    canvasRef,
    contentScale,
    setContentScale,
    minScale: effectiveZoom.min,
    maxScale: effectiveZoom.max,
    onUpdateBlockPosition, onUpdateGroupPosition,
    setIsDragging, setDragBlockId,
    isDraggingRef, dragBlockIdRef, dragOffsetRef,
    canvasOriginXRef, canvasOriginYRef,
    onSheetSwitch: handleMobileSheetSwitchInternal,
    onOpenSheetList: mobileGestures?.onOpenSheetList,
    onFitToScreen: () => handleFitToScreenRef.current(),
    onUndo: mobileGestures?.onUndo,
    onBlockLongPress: mobileGestures?.onBlockLongPress,
    onGroupCardLongPress: mobileGestures?.onGroupCardLongPress,
    onCanvasLongPress: mobileGestures?.onCanvasLongPress,
    onOpenLeftSidebar: mobileGestures?.onOpenLeftSidebar,
    onOpenRightSidebar: mobileGestures?.onOpenRightSidebar,
    onOpenTopBar: mobileGestures?.onOpenTopBar,
    onGroupDragStart: (gid, sx, sy) => handleGroupDragStartFromCoordsRef.current(gid, sx, sy),
    onGroupDragMove: (gid, x, y) => handleGroupDragMoveFromCoordsRef.current(gid, x, y),
    onBlockDragCommit: (blockId, sx, sy) => {
      const synthetic = {
        clientX: sx, clientY: sy,
        stopPropagation: () => {}, preventDefault: () => {},
      } as unknown as React.MouseEvent;
      handleBlockDragStart(blockId, synthetic);
    },
    onCollapsedGroupDragCommit: (groupId, sx, sy) => {
      const synthetic = {
        clientX: sx, clientY: sy,
        stopPropagation: () => {}, preventDefault: () => {},
      } as unknown as React.MouseEvent;
      handleCollapsedGroupDragStart(groupId, synthetic);
    },
    pathRefs: gesturePathRefs,
  });

  const handleCanvasKeyDown = useCanvasKeyboardNav({ visibleBlocks, selectedBlockId, onSelectBlock });

  const handleBlockResize = useCallback((blockId: string, width: number, height: number) => {
    setBlockDimensions(prev => {
      const existing = prev[blockId];
      if (existing && existing.width === width && existing.height === height) return prev;
      return { ...prev, [blockId]: { width, height } };
    });
  }, []);

  // Connectors får referera Block ELLER Group-id (per type-kontrakt i
  // workflow.ts:129-130). buildAnchorIndex exponerar pseudo-block-entries
  // för båda group-typerna så ConnectorPath:s `find(id)` hittar dem
  // oavsett om endpoint är Block, collapsed Group (Layer) eller expanded
  // Group. Pure helper — testbar utan React.
  const { items: allRenderableBlocks, dims: allBlockDimensions } = useMemo(
    () => buildAnchorIndex({
      visibleBlocks,
      allBlocks: blocks,
      collapsedGroups,
      expandedGroups,
      blockDimensions,
    }),
    [visibleBlocks, blocks, collapsedGroups, expandedGroups, blockDimensions],
  );

  // Fit-to-screen — beräknar scale + scroll-target för att passa allt
  // innehåll i viewporten. Klampar inom effectiveZoom-range (tier-min,
  // men mobile får automatiskt viewport-fit-floor). Om innehållet inte
  // ryms vid min → scale = min och centroid hamnar i mitten av viewport —
  // user scrollar resten. Mobile-floor beräknas runtime så fit alltid
  // kan visa allt.
  const handleFitToScreen = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const bounds = computeContentBounds(blocks, groups, allBlockDimensions);
    if (!bounds) return;
    // Mobile får utökad floor om content > viewport (annars är tier-min OK).
    const mobileFloor = isMobile
      ? Math.min(
          effectiveZoom.min,
          Math.min(canvas.clientWidth / bounds.width, canvas.clientHeight / bounds.height),
        )
      : effectiveZoom.min;
    const fit = fitToScreen(
      bounds,
      { width: canvas.clientWidth, height: canvas.clientHeight },
      { zoomMin: mobileFloor, zoomMax: effectiveZoom.max },
    );
    if (!fit) return;
    setContentScale(fit.scale);
    requestAnimationFrame(() => {
      // World-koord (fit.scrollTo) konverteras till canvas-CSS via
      // (worldX + originX). Sen multipliceras med scale för att hamna
      // på post-transform-pixel-koordinat.
      const targetScreenX = (fit.scrollTo.x + canvasOriginX) * fit.scale;
      const targetScreenY = (fit.scrollTo.y + canvasOriginY) * fit.scale;
      canvas.scrollTo({
        left: Math.max(0, targetScreenX - canvas.clientWidth / 2),
        top: Math.max(0, targetScreenY - canvas.clientHeight / 2),
        behavior: 'smooth',
      });
    });
  }, [blocks, groups, allBlockDimensions, effectiveZoom.min, effectiveZoom.max, setContentScale, canvasOriginX, canvasOriginY, isMobile]);

  // Bind ref för canvas-touch.onFitToScreen (definerad ovan useCanvasTouchGestures-call:en).
  useEffect(() => { handleFitToScreenRef.current = handleFitToScreen; }, [handleFitToScreen]);

  const zoomIn = handleScaleUp;
  const zoomOut = handleScaleDown;
  const zoomReset = handleResetScale;

  useEffect(() => {
    onRegisterZoomHandlers?.({ zoomIn, zoomOut, resetZoom: zoomReset, fitToScreen: handleFitToScreen });
  }, [onRegisterZoomHandlers, zoomIn, zoomOut, zoomReset, handleFitToScreen]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    if (viewOnly) return;
    e.preventDefault();
    const type = e.dataTransfer.getData('blockType') as BlockType;
    if (!type || !canvasRef.current) return;
    const label = e.dataTransfer.getData('toolLabel') || undefined;
    const canvasInner = canvasRef.current.querySelector('.canvas-inner') as HTMLElement;
    if (!canvasInner) return;
    const innerRect = canvasInner.getBoundingClientRect();
    const origin = getOrigin();
    const x = (e.clientX - innerRect.left) / contentScale - origin.x;
    const y = (e.clientY - innerRect.top) / contentScale - origin.y;
    onCreateBlock(type, x, y, label);
    dispatchOnboardingEvent({ name: 'tool:dropped', target: type });
  }, [viewOnly, onCreateBlock, contentScale, getOrigin]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    if (viewOnly) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  }, [viewOnly]);

  // Bind group-drag-refs så touch-hooken läser current-impl efter varje
  // re-render (groups/blocks/state ändras → callback omskrivs).
  useEffect(() => { handleGroupDragStartFromCoordsRef.current = handleGroupDragStartFromCoords; }, [handleGroupDragStartFromCoords]);
  useEffect(() => { handleGroupDragMoveFromCoordsRef.current = handleGroupDragMoveFromCoords; }, [handleGroupDragMoveFromCoords]);

  useEffect(() => {
    onMinimapDataChange({
      canvasWidth: dynamicCanvasWidth,
      canvasHeight: dynamicCanvasHeight,
      viewportWidth: viewportSize.width,
      viewportHeight: viewportSize.height,
      scrollLeft: scrollPosition.left,
      scrollTop: scrollPosition.top,
      zoom: contentScale,
      blockDimensions,
      onNavigate: handleMinimapNavigate,
    });
  }, [viewportSize, scrollPosition, contentScale, handleMinimapNavigate, onMinimapDataChange, blockDimensions, dynamicCanvasWidth, dynamicCanvasHeight]);

  const isInsideLayer = layerPath.length > 0;

  return (
    <div
      ref={canvasRef}
      role="application"
      aria-label={`Workflow canvas med ${blocks.length} block och ${visibleConnectors.length} kopplingar`}
      data-guide="canvas"
      tabIndex={0}
      className="flex-1 overflow-auto bg-card relative touch-none min-h-0 focus-visible:outline-2 focus-visible:outline-primary"
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onKeyDown={handleCanvasKeyDown}
    >
      <CanvasHeader
        breadcrumbs={breadcrumbs}
        onNavigateToLevel={onNavigateToLevel}
        contentScale={contentScale}
        minScale={effectiveZoom.min}
        maxScale={effectiveZoom.max}
        onScaleUp={zoomIn}
        onScaleDown={zoomOut}
        onResetScale={zoomReset}
        onFitToScreen={handleFitToScreen}
        zoomMode={effectiveZoom.mode}
        hideScaleControls={hideHeaderScaleControls || enableFloatingHud}
      />

      {enableFloatingHud && (
        <CanvasHud
          contentScale={contentScale}
          minScale={effectiveZoom.min}
          maxScale={effectiveZoom.max}
          zoomMode={effectiveZoom.mode}
          onScaleUp={zoomIn}
          onScaleDown={zoomOut}
          onResetScale={zoomReset}
          onFitToScreen={handleFitToScreen}
        />
      )}

      <CanvasContextMenu
        onContextMenuCapture={handleCanvasContextMenuCapture}
        toolTypes={toolTypes}
        onAddBlock={handleAddBlockFromMenu}
        onResetZoom={handleResetScale}
        disabled={viewOnly || toolTypes.length === 0}
      >
      <div
        // Layout-bounds-wrapper. Static-mode: explicit scaled width/height
        // (canvas-inner är CSS-transform-skalad → utan wrapper:n skulle
        // outer scroll-area inte matcha visuell storlek).
        // Dynamic-mode: `display: contents` så div:en inte påverkar layout
        // alls — pre-split hade ingen wrapper, canvas-inner satt direkt
        // under canvasRef. `contents` gör elementet osynligt för layout
        // utan att vi behöver duplicera JSX-grenarna.
        className={isStaticMode ? "m-6" : undefined}
        style={{
          display: isStaticMode ? undefined : 'contents',
          width:  isStaticMode ? dynamicCanvasWidth  * contentScale : undefined,
          height: isStaticMode ? dynamicCanvasHeight * contentScale : undefined,
          transition: isStaticMode ? 'width 120ms ease-out, height 120ms ease-out' : undefined,
        }}
      >
      <div
        ref={canvasInnerRef}
        // Both modes har container-styling (border, rounded, shadow, grid-bg).
        // Static: m-6 ligger på wrappern (för layout-bounds-mathen).
        // Dynamic: m-6 ligger HÄR — pre-split-konventionen där canvas-inner
        //   är "papperet" man pannar runt på.
        className={isStaticMode
          ? "canvas-inner relative rounded-xl border-2 border-border shadow-lg"
          : "canvas-inner relative rounded-xl border-2 border-border shadow-lg m-6"}
        style={{
          width: dynamicCanvasWidth,
          height: dynamicCanvasHeight,
          // Static-mode: transform på canvas-inner så HELA boxen (border,
          // grid, content) krymper uniformt. Dynamic-mode: ingen transform
          // här — canvas-root inuti gör scale + coord-rymd-expansion.
          transform: isStaticMode ? `scale(${contentScale})` : undefined,
          transformOrigin: 'top left',
          transition: isStaticMode ? 'transform 120ms ease-out' : undefined,
          // A-024: canvas owns all touch gestures (pan, pinch-zoom, block
          // drag) via useCanvasTouchGestures. `touch-action: none` prevents
          // iOS native gestures from hijacking pinch-zoom before our
          // touchmove-handler calls preventDefault on the first event.
          touchAction: 'none',
          backgroundImage: [
            `linear-gradient(to right, hsl(var(--canvas-grid) / 0.3) 1px, transparent 1px)`,
            `linear-gradient(to bottom, hsl(var(--canvas-grid) / 0.3) 1px, transparent 1px)`,
            `linear-gradient(to right, hsl(var(--canvas-grid)) 1px, transparent 1px)`,
            `linear-gradient(to bottom, hsl(var(--canvas-grid)) 1px, transparent 1px)`,
          ].join(', '),
          backgroundSize: `${GRID_SIZE_PX}px ${GRID_SIZE_PX}px, ${GRID_SIZE_PX}px ${GRID_SIZE_PX}px, ${GRID_SIZE_PX * 5}px ${GRID_SIZE_PX * 5}px, ${GRID_SIZE_PX * 5}px ${GRID_SIZE_PX * 5}px`,
          borderColor: isInsideLayer ? 'hsl(var(--primary) / 0.3)' : undefined,
        }}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onClick={handleCanvasClick}
      >
        <div
          data-canvas-root
          className="absolute inset-0 origin-top-left pointer-events-none"
          style={{
            // Static-mode: transformen ligger på canvas-inner (ovan) →
            // ingen scale här. Dynamic-mode: scale + växande coord-rymd
            // (1920/0.5=3840) så block-positions sprids ut.
            // Transition-on när variant='dynamic-with-transition' (pre-split
            // look, block kan hoppa pga multiplikations-mismatch).
            // Transition-off vid 'dynamic-no-transition' (instant snap).
            transform: isStaticMode ? undefined : `scale(${contentScale})`,
            transition: isStaticMode || useDynamicTransition ? 'transform 120ms ease-out' : undefined,
            width: dynamicCanvasWidth / coordSpaceDivider,
            height: dynamicCanvasHeight / coordSpaceDivider,
          }}
        >
          <div
            className="absolute"
            style={{
              left: canvasOriginX / coordSpaceDivider,
              top: canvasOriginY / coordSpaceDivider,
            }}
          >
            {expandedGroups.map(group => {
              const frameNode = (
                <GroupFrame
                  key={group.id}
                  group={group}
                  blocks={blocks}
                  blockDimensions={blockDimensions}
                  isSelected={selectedGroupId === group.id}
                  onClick={() => handleGroupClick(group.id)}
                  onDragStart={e => handleGroupDragStart(group.id, e)}
                />
              );
              if (viewOnly || !onToggleGroupCollapse) return frameNode;
              return (
                <GroupContextMenu
                  key={`ctx-${group.id}`}
                  groupId={group.id}
                  locked={group.locked}
                  onFocusProperty={(gid, field) => {
                    onSelectGroup(gid);
                    requestAnimationFrame(() => requestAnimationFrame(() => {
                      const el = document.querySelector<HTMLElement>(
                        field === 'color'
                          ? '[data-guide="properties-color"] input'
                          : `[data-guide="properties-${field}"] input, [data-guide="properties-${field}"] textarea`,
                      );
                      el?.focus();
                      if (el && 'select' in el && typeof (el as HTMLInputElement).select === 'function') {
                        (el as HTMLInputElement).select();
                      }
                    }));
                  }}
                  onToggleCollapse={onToggleGroupCollapse}
                  onToggleLock={onToggleGroupLock}
                  onDelete={onDeleteGroup}
                >
                  {frameNode}
                </GroupContextMenu>
              );
            })}

            {visibleConnectors.map(connector => (
              <ConnectorPath
                key={connector.id}
                connector={connector}
                blocks={allRenderableBlocks}
                blockDimensions={allBlockDimensions}
                isSelected={selectedConnectorId === connector.id}
                onWaypointDragStart={viewOnly ? undefined : handleWaypointDragStart}
                onAddWaypoint={viewOnly ? undefined : handleAddWaypoint}
                onRemoveWaypoint={viewOnly ? undefined : handleRemoveWaypoint}
                onUpdateLabel={viewOnly ? undefined : onUpdateConnectorLabel}
                onSelect={viewOnly ? undefined : (id) => {
                  // Selecting a connector deselects block/group (mutex
                  // enforced upstream in SelectionModeContext setter).
                  onSelectBlock('');
                  onSelectGroup(null);
                  setSelectedConnectorId(id);
                }}
                onContextMenuRequest={
                  viewOnly || !onDeleteConnector
                    ? undefined
                    : (id, cx, cy, world, waypoints) => setConnectorMenu({
                        connectorId: id, x: cx, y: cy,
                        clickPointWorld: world, currentWaypoints: waypoints,
                      })
                }
                viewOnly={viewOnly}
              />
            ))}

            {visibleBlocks.map((block, index) => {
              const blockNode = (
                <WorkflowBlock
                  key={block.id}
                  block={block}
                  isSelected={selectedBlockId === block.id}
                  isConnecting={connectMode && connectFromId === block.id}
                  isGrouping={groupMode}
                  isInGroupSelection={groupSelection.includes(block.id)}
                  pendingConnection={connectFromId === block.id}
                  isCrossSheetMode={crossSheetMode}
                  isCrossSheetSource={crossSheetSourceBlockId === block.id}
                  onSelect={() => handleBlockSelect(block.id)}
                  onDragStart={e => handleBlockDragStart(block.id, e)}
                  onPortClick={handlePortClick}
                  onUpdateIcon={viewOnly ? undefined : onUpdateBlockIcon}
                  onResize={handleBlockResize}
                  onConvertToLayer={viewOnly ? undefined : onConvertToLayer}
                  onEditRequest={viewOnly ? undefined : (id) => onFocusBlockProperty?.(id, 'name')}
                  viewOnly={viewOnly}
                  tabIndex={selectedBlockId === block.id ? 0 : (!selectedBlockId && index === 0 ? 0 : -1)}
                  onFocus={() => onSelectBlock(block.id)}
                />
              );
              if (viewOnly || !onFocusBlockProperty) return blockNode;
              return (
                <BlockContextMenu
                  key={block.id}
                  blockId={block.id}
                  availableGroups={groups}
                  selectionIds={groupSelection}
                  onFocusProperty={(field) => onFocusBlockProperty(block.id, field)}
                  onCreateGroupFromSelection={onCreateGroupFromSelection}
                  onAddToGroup={onAddBlockToGroup}
                  onConvertToLayer={onConvertToLayer}
                  onDuplicate={onDuplicateBlock}
                  onDelete={onDeleteBlock}
                  onExplain={onExplainBlock}
                >
                  {blockNode}
                </BlockContextMenu>
              );
            })}

            {collapsedGroups.map(group => {
              const center = computeGroupCenter(group, blocks);
              const x = group.collapsedX ?? center.x;
              const y = group.collapsedY ?? center.y;
              const cardNode = (
                <CollapsedGroupCard
                  key={`collapsed-${group.id}`}
                  group={group}
                  blocks={blocks}
                  groups={groups}
                  x={x}
                  y={y}
                  isSelected={selectedGroupId === group.id}
                  onSelect={() => handleGroupClick(group.id)}
                  onDragStart={e => handleCollapsedGroupDragStart(group.id, e)}
                  onDoubleClick={() => onNavigateInto(group.id)}
                  onPortClick={handlePortClick}
                  onNavigateIntoChild={viewOnly ? undefined : onNavigateInto}
                  onResize={viewOnly ? undefined : handleBlockResize}
                />
              );
              if (viewOnly || !onFocusBlockProperty) return cardNode;
              return (
                <LayerContextMenu
                  key={`ctx-${group.id}`}
                  groupId={group.id}
                  onEnter={(gid) => onNavigateInto(gid)}
                  onFocusProperty={(gid, field) => {
                    onSelectGroup(gid);
                    requestAnimationFrame(() => requestAnimationFrame(() => {
                      const el = document.querySelector<HTMLElement>(
                        field === 'color'
                          ? '[data-guide="properties-color"] input'
                          : `[data-guide="properties-${field}"] input, [data-guide="properties-${field}"] textarea`,
                      );
                      el?.focus();
                      if (el && 'select' in el && typeof (el as HTMLInputElement).select === 'function') {
                        (el as HTMLInputElement).select();
                      }
                    }));
                  }}
                  onToggleCollapse={onToggleGroupCollapse}
                  onDelete={onDeleteGroup}
                >
                  {cardNode}
                </LayerContextMenu>
              );
            })}
          </div>
        </div>
      </div>
      </div>
      </CanvasContextMenu>
      {/* C6: connector right-click meny — floating panel vid cursor-pos. */}
      <ConnectorContextMenu
        state={connectorMenu}
        onClose={() => setConnectorMenu(null)}
        onDelete={(id) => onDeleteConnector?.(id)}
        onAddWaypoint={viewOnly ? undefined : handleAddWaypoint}
        onClearLabel={onUpdateConnectorLabel ? (id) => onUpdateConnectorLabel(id, '') : undefined}
      />
      {/* Connector properties moved to right-side PropertiesPanel via
          SelectionModeContext — single-click on a line now selects it
          like a block instead of opening a floating popover. */}

      {/* Mobile gesture-overlays: live svans under 1-finger-svep +
          sheet-byte-bekräftelse. Båda render:ar bara på mobil och har
          pointer-events: none så de stör inte touch-events. */}
      {isMobile && (
        <>
          <GestureTrail pathRef={gesturePathRef} tickRef={gestureTickRef} />
          <SheetSwitchAnimation trigger={sheetSwitchTrigger} />
        </>
      )}
      <CanvasEmptyState
        isEmpty={blocks.length === 0 && !viewOnly}
        onOpenWizard={onReopenWizard}
        viewport={{
          scrollLeft: scrollPosition.left,
          scrollTop: scrollPosition.top,
          width: viewportSize.width,
          height: viewportSize.height,
        }}
      />
    </div>
  );
}
